/* ============================================================
   CRUXSTAR PREMIUM LAYER · v1
   Additive behaviour only. Never blocks render. Bails out
   entirely when the visitor asks for reduced motion.
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------- 1. SPLIT-TEXT REVEAL ---------------------------
     Wrap each word in a masked span, stagger it in on scroll. */
  function splitText() {
    var sel = [
      '.banner__heading',
      '.rich-text__heading',
      '.image-with-text__heading',
      '.multicolumn .title'
    ].join(',');

    document.querySelectorAll(sel).forEach(function (el) {
      if (el.dataset.cxpSplit) return;
      if (el.querySelector('img, svg, video')) return;
      var text = el.textContent.replace(/\s+/g, ' ').trim();
      if (!text || text.length > 220) return;

      el.dataset.cxpSplit = '1';
      var words = text.split(' ');
      el.textContent = '';

      words.forEach(function (w, i) {
        var mask = document.createElement('span');
        mask.className = 'cxp-split';
        var inner = document.createElement('span');
        inner.style.setProperty('--i', i);
        inner.textContent = w;
        mask.appendChild(inner);
        el.appendChild(mask);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });
    });
  }

  /* ---------- 2. INTERSECTION REVEALS -----------------------
     threshold 0 so ANY sliver counts — a section taller than the
     viewport can never reach a high ratio and would otherwise
     stay hidden forever. Plus a hard failsafe: anything still
     unrevealed after 2.5s gets shown regardless. Content being
     visible always beats content being animated. */
  function revealAll() {
    document.querySelectorAll('.cxp-reveal, [data-cxp-split]').forEach(function (n) {
      n.classList.add('cxp-in');
    });
  }

  function observeReveals() {
    if (!('IntersectionObserver' in window)) { revealAll(); return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('cxp-in');
        io.unobserve(e.target);
      });
    }, { threshold: 0, rootMargin: '0px 0px -2% 0px' });

    document.querySelectorAll('[data-cxp-split], .cxp-reveal').forEach(function (n) {
      if (n.classList.contains('cxp-in')) return;
      io.observe(n);
    });

    // failsafe — never let the effect swallow the content
    clearTimeout(observeReveals._t);
    observeReveals._t = setTimeout(revealAll, 2500);
  }

  /* ---------- 3. MEDIA REVEAL TAGGING -----------------------
     Only tag when we can actually guarantee a reveal. */
  function tagMedia() {
    if (!('IntersectionObserver' in window)) return;
    var sel = [
      '.image-with-text__media',
      '.multicolumn-card__image-wrapper',
      '.banner__media',
      '.collage__item'
    ].join(',');
    document.querySelectorAll(sel).forEach(function (n) { n.classList.add('cxp-reveal'); });
  }

  /* ---------- 4. CLIENT MARQUEE ------------------------------
     Upgrades any element carrying data-cxp-marquee="A · B · C"
     into a seamless infinite ticker. */
  function marquee() {
    document.querySelectorAll('[data-cxp-marquee]').forEach(function (host) {
      if (host.dataset.cxpDone) return;
      host.dataset.cxpDone = '1';

      var names = host.getAttribute('data-cxp-marquee')
        .split(/[·|,]/).map(function (s) { return s.trim(); }).filter(Boolean);
      if (!names.length) return;

      var wrap = document.createElement('div');
      wrap.className = 'cxp-marquee' + (host.hasAttribute('data-cxp-marquee-rev') ? ' cxp-marquee--rev' : '');
      var track = document.createElement('div');
      track.className = 'cxp-marquee__track';

      // duplicated once so the -50% translate loops seamlessly
      for (var pass = 0; pass < 2; pass++) {
        names.forEach(function (n) {
          var item = document.createElement('span');
          item.className = 'cxp-marquee__item';
          item.textContent = n;
          if (pass === 1) item.setAttribute('aria-hidden', 'true');
          track.appendChild(item);
        });
      }
      wrap.appendChild(track);
      host.innerHTML = '';
      host.appendChild(wrap);
    });
  }

  /* ---------- 6. SCROLL PROGRESS ---------------------------- */
  function progress() {
    var bar = document.createElement('div');
    bar.className = 'cxp-progress';
    document.body.appendChild(bar);
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var max = document.documentElement.scrollHeight - window.innerHeight;
        bar.style.transform = 'scaleX(' + (max > 0 ? window.scrollY / max : 0) + ')';
        ticking = false;
      });
    }, { passive: true });
  }

  /* ---------- 7. FILM GRAIN --------------------------------- */
  function grain() {
    var g = document.createElement('div');
    g.className = 'cxp-grain';
    g.setAttribute('aria-hidden', 'true');
    document.body.appendChild(g);
  }

  /* ---------- 8. MAGNETIC BUTTONS --------------------------- */
  function magnetic() {
    if (!window.matchMedia('(hover:hover) and (pointer:fine)').matches) return;
    document.querySelectorAll('.button').forEach(function (btn) {
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * 0.16;
        var y = (e.clientY - r.top - r.height / 2) * 0.28;
        btn.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
      });
      btn.addEventListener('pointerleave', function () { btn.style.transform = ''; });
    });
  }

  /* ---------- 9. CURSOR SPOTLIGHT --------------------------- */
  function spotlight() {
    if (!window.matchMedia('(hover:hover) and (pointer:fine)').matches) return;
    var el = document.createElement('div');
    el.className = 'cxp-spot';
    el.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el);

    var x = 0, y = 0, cx = 0, cy = 0, running = false;
    document.addEventListener('pointermove', function (e) {
      x = e.clientX; y = e.clientY;
      el.classList.add('cxp-spot--on');
      if (!running) { running = true; requestAnimationFrame(loop); }
    }, { passive: true });

    function loop() {
      cx += (x - cx) * 0.13;
      cy += (y - cy) * 0.13;
      el.style.transform = 'translate3d(' + cx + 'px,' + cy + 'px,0)';
      if (Math.abs(x - cx) > 0.4 || Math.abs(y - cy) > 0.4) requestAnimationFrame(loop);
      else running = false;
    }
    document.addEventListener('pointerleave', function () { el.classList.remove('cxp-spot--on'); });
  }

  /* ---------- 10. PARALLAX ---------------------------------- */
  function parallax() {
    var nodes = [].slice.call(document.querySelectorAll(
      '.image-with-text__media img, .banner__media img, .collage__item img'
    ));
    if (!nodes.length) return;
    nodes.forEach(function (n) { n.classList.add('cxp-para'); });

    var ticking = false;
    function frame() {
      var vh = window.innerHeight;
      nodes.forEach(function (n) {
        var r = n.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        var mid = r.top + r.height / 2;
        var off = ((mid - vh / 2) / vh) * -22; // px drift
        n.style.transform = 'translate3d(0,' + off.toFixed(2) + 'px,0) scale(1.06)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(frame);
    }, { passive: true });
    frame();
  }

  /* ---------- 12. COUNT-UP STATS ---------------------------- */
  function counters() {
    if (!('IntersectionObserver' in window)) return;
    var re = /(\$?)([\d][\d,\.]*)\s*(M\+|K\+|M|K|\+|%)?/;
    var candidates = [].slice.call(document.querySelectorAll(
      '.rich-text__text strong, .rich-text__heading, .multicolumn-card__info strong'
    )).filter(function (n) {
      var t = n.textContent.trim();
      return t.length < 22 && re.test(t) && /\d/.test(t) && !n.dataset.cxpCount;
    });
    if (!candidates.length) return;

    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        var n = e.target;
        io.unobserve(n);
        if (n.dataset.cxpCount) return;
        n.dataset.cxpCount = '1';
        var m = n.textContent.trim().match(re);
        if (!m) return;
        var pre = m[1] || '', suf = m[3] || '';
        var target = parseFloat(m[2].replace(/,/g, ''));
        if (!isFinite(target) || target <= 0) return;
        var tail = n.textContent.slice(n.textContent.indexOf(m[0]) + m[0].length);
        var head = n.textContent.slice(0, n.textContent.indexOf(m[0]));
        n.classList.add('cxp-count');
        var t0 = null, dur = 1500;
        function step(ts) {
          if (t0 === null) t0 = ts;
          var p = Math.min((ts - t0) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          var v = target * eased;
          var shown = target >= 100 ? Math.round(v).toLocaleString() : v.toFixed(1);
          n.textContent = head + pre + shown + suf + tail;
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.4 });
    candidates.forEach(function (n) { io.observe(n); });
  }


  /* ---------- 13. AI-GEN RICH TEXT BLOCK ------------------- */
  function aiBlocks() {
    var heroes = document.querySelectorAll('[class*="ai-agency-richtext-hero-"]');
    if (!heroes.length) return;

    Array.prototype.forEach.call(heroes, function (hero) {
      var host = hero.closest('[class*="ai-agency-richtext-"]');
      while (host && host.parentElement &&
             /ai-agency-richtext-(container|hero)-/.test(host.className)) {
        host = host.parentElement;
      }
      if (!host || host.dataset.cxpAi) return;
      host.dataset.cxpAi = '1';
      host.classList.add('cxp-aiblock', 'cxp-reveal');

      var order = ['hero', 'subheading', 'body', 'divider', 'stat', 'closing'];
      order.forEach(function (name, i) {
        var el = host.querySelector('[class*="ai-agency-richtext-' + name + '-"]');
        if (el) el.style.setProperty('--cxi', i);
      });

      aiHeroWords(host);
      aiStatCount(host);
    });
  }

  /* Word-by-word rise on the hero line only.
     NOT the subheading — that one carries a background-clip:text
     gradient, and splitting text inside one of those collapses
     every word to x=0. Learned that the hard way. */
  function aiHeroWords(host) {
    var hero = host.querySelector('[class*="ai-agency-richtext-hero-"]');
    if (!hero || hero.dataset.cxpAiSplit) return;
    if (hero.querySelector('img, svg, video')) return;
    var text = hero.textContent.replace(/\s+/g, ' ').trim();
    if (!text || text.length > 200) return;

    hero.dataset.cxpAiSplit = '1';
    var words = text.split(' ');
    hero.textContent = '';
    words.forEach(function (w, i) {
      var mask = document.createElement('span');
      mask.className = 'cxp-aiw';
      var inner = document.createElement('span');
      inner.style.setProperty('--w', i);
      inner.textContent = w;
      mask.appendChild(inner);
      hero.appendChild(mask);
      if (i < words.length - 1) hero.appendChild(document.createTextNode(' '));
    });
  }

  /* Count the stat up from zero when it scrolls in. */
  function aiStatCount(host) {
    var el = host.querySelector('[class*="ai-agency-richtext-stat-"]');
    if (!el || el.dataset.cxpNum) return;
    var raw = el.textContent;
    var m = raw.match(/^\s*(\$?)([\d][\d,\.]*)\s*(M\+|K\+|B\+|M|K|B|%|\+)?/);
    if (!m) return;

    var pre = m[1] || '', suf = m[3] || '';
    var target = parseFloat(m[2].replace(/,/g, ''));
    if (!isFinite(target) || target <= 0) return;

    el.dataset.cxpNum = '1';
    var tail = raw.slice(m[0].length);
    var fmt = function (v) {
      return pre + (target >= 100 ? Math.round(v).toLocaleString() : v.toFixed(1)) + suf;
    };

    el.textContent = '';
    var num = document.createElement('span');
    num.className = 'cxp-ainum';
    num.textContent = fmt(target);
    el.appendChild(num);
    el.appendChild(document.createTextNode(tail));

    if (!('IntersectionObserver' in window)) return;  // final value already shown

    num.textContent = fmt(0);
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(el);
        var t0 = null, dur = 1800;
        (function step(ts) {
          if (t0 === null) t0 = ts;
          var p = Math.min((ts - t0) / dur, 1);
          num.textContent = fmt(target * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(step);
          else num.classList.add('cxp-ainum--done');
        })(performance.now());
      });
    }, { threshold: 0.35 });
    io.observe(el);

    // failsafe: never leave a zero on screen
    setTimeout(function () {
      if (!num.classList.contains('cxp-ainum--done') && num.textContent === fmt(0)) {
        num.textContent = fmt(target);
      }
    }, 4000);
  }

  ready(function () {
    // marquee runs even under reduced motion — it just doesn't scroll
    marquee();
    if (REDUCED) return;

    // Only now do we let the CSS hide anything. If this line never
    // runs, every image on the site is simply visible.
    document.documentElement.classList.add('cxp-js');

    splitText();
    tagMedia();
    aiBlocks();
    observeReveals();
    progress();
    grain();
    magnetic();
    spotlight();
    parallax();
    counters();
  });

  // last-resort net: once everything has loaded, nothing stays hidden
  window.addEventListener('load', function () {
    setTimeout(revealAll, 1200);
  });

  // re-run for sections re-rendered by the theme editor
  document.addEventListener('shopify:section:load', function () {
    marquee();
    aiBlocks();
    if (REDUCED) return;
    splitText();
    tagMedia();
    observeReveals();
    magnetic();
  });
})();
